import {
  apiGet,
  apiPost,
} from './client'
import type {
  PublicFacility,
  PluginSettings,
  PublicBranch,
  PublicReservationProduct,
  PublicEmployee,
  CapacityInfoResponse,
  CreateReservationRequest,
  ReservationResponse,
  PaymentStatusResponse,
  Workshop,
  AvailabilityApiResponse,
} from '@/types/api'

export function getFacility(slug: string) {
  return apiGet<PublicFacility>(`/api/public/facilities/${encodeURIComponent(slug)}`)
}

export function getPluginSettings(slug: string) {
  return apiGet<PluginSettings>(`/api/public/facilities/${encodeURIComponent(slug)}/plugin-settings`)
}

export function getBranches(facilityId: number) {
  return apiGet<PublicBranch[]>('/api/public/branches', { facility_id: facilityId })
}

export function getBranch(id: number) {
  return apiGet<PublicBranch>(`/api/public/branches/${id}`)
}

export function getServicesPackages(branchId: number, source?: string) {
  return apiGet<PublicReservationProduct[]>('/api/public/services-packages', {
    branch_id: branchId,
    ...(source ? { source } : {}),
  })
}

export function getWorkshops(branchId: number, dateFrom?: string) {
  return apiGet<Workshop[]>('/api/public/workshops', {
    branch_id: branchId,
    ...(dateFrom ? { date_from: dateFrom } : {}),
  })
}

export function getWorkshop(id: number) {
  return apiGet<Workshop>(`/api/public/workshops/${id}`)
}

export function getEmployees(params: {
  branch_id: number
  service_id?: number
  package_id?: number
  slot?: string
}) {
  return apiGet<PublicEmployee[]>('/api/public/employees', params as Record<string, string | number>)
}

export function getAvailabilityForDate(params: {
  branch_id: number
  service_id?: number
  package_id?: number
  date: string
  employee_id?: number
  include_prices?: boolean
}) {
  const query: Record<string, string | number | boolean> = {
    branch_id: params.branch_id,
    date: params.date,
  }
  if (params.service_id != null) query.service_id = params.service_id
  if (params.package_id != null) query.package_id = params.package_id
  if (params.employee_id != null) query.employee_id = params.employee_id
  if (params.include_prices === false) query.include_prices = 0
  return apiGet<AvailabilityApiResponse>(
    '/api/public/reservations/availability',
    query as Record<string, string | number>
  )
}

export function getCapacityInfo(params: Record<string, string | number>) {
  return apiGet<CapacityInfoResponse>('/api/public/reservations/capacity-info', params)
}

export function createReservation(body: CreateReservationRequest) {
  return apiPost<ReservationResponse>('/api/public/reservations', body)
}

export function getPaymentStatus(reservationId: number) {
  return apiGet<PaymentStatusResponse>(
    `/api/public/reservations/${reservationId}/payments/status`
  )
}

export function initiatePayment(reservationId: number, body?: Record<string, unknown>) {
  return apiPost<PaymentStatusResponse>(
    `/api/public/reservations/${reservationId}/payments/initiate`,
    body ?? {}
  )
}

export interface InitiateWithPaymentRequest {
  reservation_data: CreateReservationRequest
  payment_method: 'card' | 'apple_pay' | 'google_pay'
  token?: Record<string, unknown>
  initial_url?: string
}

export interface InitiateWithPaymentResponse {
  reservation_id: number
  payment_url?: string | null
  requires_redirect?: boolean
}

export function initiatePaymentWithReservation(
  body: InitiateWithPaymentRequest
) {
  return apiPost<InitiateWithPaymentResponse>(
    '/api/public/reservations/initiate-with-payment',
    body
  )
}

export function verifyZlavaDnaCoupon(body: {
  branch_id: number
  service_id?: number
  package_id?: number
  coupon_code: string
  coupon_password: string
  zd_deal_item_id: string
}) {
  return apiPost<unknown>('/api/public/reservations/zlavadna/verify', body)
}

export interface ResolveZlavaDnaServiceResponse {
  service_id?: number
  package_id?: number
  branch_id?: number
  branch_ids?: number[]
  [key: string]: unknown
}

export function resolveZlavaDnaService(zdId: string) {
  return apiGet<ResolveZlavaDnaServiceResponse>(
    '/api/public/resolve-zd-service',
    { zd_id: zdId }
  )
}

export function getFacilitySlugByBranch(branchId: number) {
  return apiGet<{ slug: string }>(`/api/public/facility-slug-by-branch/${branchId}`)
}

export function getFacilitySlugByService(serviceId: string, source?: string) {
  const path = `/api/public/facility-slug-by-service/${encodeURIComponent(serviceId)}`
  return apiGet<{ slug: string }>(
    source ? `${path}?source=${encodeURIComponent(source)}` : path
  )
}

export interface PaymentMethodItem {
  id: number
  name: string
  [key: string]: unknown
}

export function getPaymentMethods() {
  return apiGet<{ data: PaymentMethodItem[] }>('/api/public/payment-methods')
}
